import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import "./FeatureFlagsTab.css";

// Admin-only feature flag switchboard. Flags are registered in the
// backend (FEATURE_FLAG_DEFS in graphql-schema.js) — this tab just
// lists them with toggles. Flipping one takes effect for users within
// ~30 seconds (the Lambda caches reads that long) plus whenever their
// page next fetches flags.

const FEATURE_FLAGS = gql`
  query FeatureFlagsAdmin {
    featureFlags {
      name
      value
      description
      updatedAt
      updatedBy
    }
  }
`;

const SET_FLAG = gql`
  mutation SetFeatureFlag($name: String!, $value: Boolean!) {
    setFeatureFlag(name: $name, value: $value) {
      name
      value
      description
      updatedAt
      updatedBy
    }
  }
`;

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const FeatureFlagsTab = () => {
  const { data, loading, error, refetch } = useQuery(FEATURE_FLAGS, {
    fetchPolicy: "network-only",
  });
  const [setFlag, { loading: saving }] = useMutation(SET_FLAG);

  const flags = data?.featureFlags || [];

  const handleToggle = async (flag) => {
    const next = !flag.value;
    if (
      !window.confirm(
        `Turn ${flag.name} ${next ? "ON" : "OFF"}?\n\nUsers see the change within about 30 seconds.`
      )
    ) {
      return;
    }
    try {
      await setFlag({ variables: { name: flag.name, value: next } });
      refetch();
    } catch (err) {
      window.alert(`Couldn't update flag: ${err.message || err}`);
    }
  };

  return (
    <div className="flags-tab">
      <div className="flags-tab__header">
        <h2 className="flags-tab__title">Feature Flags</h2>
        <button
          type="button"
          className="flags-tab__btn"
          onClick={() => refetch()}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <p className="flags-tab__hint">
        Site-wide switches that change what users see, live — no deploy
        needed. Changes reach users within ~30 seconds. New flags are added
        in code (FEATURE_FLAG_DEFS in the graphql service) and appear here
        automatically.
      </p>

      {error && (
        <div className="flags-tab__error">
          Error loading flags: {error.message}
        </div>
      )}

      {!loading && !error && flags.length === 0 && (
        <div className="flags-tab__empty">No flags registered yet.</div>
      )}

      <div className="flags-tab__list">
        {flags.map((flag) => (
          <div key={flag.name} className="flags-tab__row">
            <div className="flags-tab__row-main">
              <div className="flags-tab__name">
                <code>{flag.name}</code>
                <span
                  className={
                    flag.value
                      ? "flags-tab__state flags-tab__state--on"
                      : "flags-tab__state flags-tab__state--off"
                  }
                >
                  {flag.value ? "ON" : "OFF"}
                </span>
              </div>
              {flag.description && (
                <div className="flags-tab__description">{flag.description}</div>
              )}
              {flag.updatedAt && (
                <div className="flags-tab__meta">
                  Last changed {formatWhen(flag.updatedAt)}
                  {flag.updatedBy ? ` by ${flag.updatedBy}` : ""}
                </div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={flag.value}
              className={
                flag.value
                  ? "flags-tab__toggle flags-tab__toggle--on"
                  : "flags-tab__toggle"
              }
              onClick={() => handleToggle(flag)}
              disabled={saving}
              title={flag.value ? "Turn off" : "Turn on"}
            >
              <span className="flags-tab__knob" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureFlagsTab;
